import React, { useState, useEffect, useMemo, useRef } from 'react'
import './MainHub.css'
import { useAuth } from '../../Auth';
import '../../client-dashboard/clientDashboardMap.css';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import { useLocation } from "react-router-dom";
import { useDetectorRelations } from '../HardwareTab/useDetectorRelations';

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap, LeafletEvent } from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';

// Assets
import fnnPopupLogo from '../../img/FNNLogoWithBlackFont-06.png'
import greenMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-grey.png'
import googleMapsLogo from '../../backend/assets/google-map-logo.png'

// Icons
import { FaChevronDown, FaChevronUp, FaCircle } from "react-icons/fa";

// Define the Detector type for TypeScript
interface DetectorObject {
    mac: string;
    ip: string;
    last_active: string;
    online: boolean;
    status: {
        site_name: string;
        gps_fix: boolean;
        gps_lat: number;
        gps_long: number;
        up_since: string;
        memory: string;
        fw_version: string;
        test_mode: boolean;
        thr: string;
        linux: boolean;
        sim_card_no?: string;
    };
}

interface RegionCountry {
    GROUP_NAME: string;
    Belongs_to: string;
}

// Search cities and addresses logic
interface Result {
    lon: string;
    lat: string;
    display_name: string;
    boundingbox: string[];
}

const DjangoNominatimProvider = {
    search: (() => {
        let timeout: NodeJS.Timeout | null = null;
        let controller: AbortController | null = null;

        return ({ query }: { query: string }) => {
            if (!query) return Promise.resolve([]);

            if (controller) controller.abort();
            if (timeout) clearTimeout(timeout);

            return new Promise((resolve, reject) => {
                timeout = setTimeout(() => {
                    controller = new AbortController();

                    fetch(`/api/geocode?q=${encodeURIComponent(query)}`, {
                        signal: controller.signal,
                    })
                        .then((response) => response.json())
                        .then((results: Result[]) => {
                            resolve(
                                results.map((r) => ({
                                    x: parseFloat(r.lon),
                                    y: parseFloat(r.lat),
                                    label: r.display_name,
                                    bounds: [
                                        [parseFloat(r.boundingbox[0]), parseFloat(r.boundingbox[2])],
                                        [parseFloat(r.boundingbox[1]), parseFloat(r.boundingbox[3])],
                                    ],
                                    raw: r,
                                }))
                            );
                        })
                        .catch((e) => {
                            if ((e as any).name === "AbortError") return;
                            reject(e);
                        });
                }, 200);
            });
        };
    })(),
};

const SearchControl: React.FC = () => {
    const map = useMap();

    useEffect(() => {
        const searchControl = new (GeoSearchControl as any)({
            provider: DjangoNominatimProvider,
            style: "bar",
            showMarker: false,
            showPopup: true,
            popupFormat: ({ query, result }: { query: string; result: { label: string } }) =>
                `You searched for: ${query}<br>Result: ${result.label}`,
            searchLabel: "Search Address or Zip",
        });

        map.addControl(searchControl);

        return () => {
            map.removeControl(searchControl);
        };
    }, [map]);

    return null;
};

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50]
});

export const greenIcon = new Icon({
    iconUrl: greenMarker,
    iconSize: [50, 50]
});

const getColorForGPSFix = (isOn: boolean) => {
    if (isOn === false) return "red";
    if (isOn === true) return "#008000";
};

const getColorForMarker = (isOn: boolean, memoryValue: string | number) => {
    if (isOn === false) return BwIcon;
    if (isOn === true) return greenIcon;
};

interface MainHubProps {
    onRegionChange?: (regions: string[]) => void;
}

const MainHub: React.FC<MainHubProps> = ({ onRegionChange }) => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const { getDashboardPermissions } = useAuth();
    const { detectorCodeObj } = useDetectorRelations();

    // --- Add this: pre-compute latestCodes the same way useDetectorRelations does ---
    const latestCodes = useMemo(() => {
        const safe = Array.isArray(detectorCodeObj) ? detectorCodeObj : [];
        return Object.values(
            safe.reduce((acc, c) => {
                const key = c.receiver?.toLowerCase().replace(/^(0x|000)/, "") || "";
                acc[key] = c;
                return acc;
            }, {} as Record<string, typeof detectorCodeObj[0]>)
        );
    }, [detectorCodeObj]);

    interface EnrichedDetector extends DetectorObject {
        code?: string;
        codeName?: string;
        codeId?: string;
        codeEntry?: typeof detectorCodeObj[0];
    }

    const enrichDetector = (det: DetectorObject): EnrichedDetector => {
        const normalizedMac = det.mac?.toLowerCase().replace(/^(0x|000)/, "") || "";
        const codeEntry = latestCodes.find(
            (c) => (c.receiver?.toLowerCase().replace(/^(0x|000)/, "") || "") === normalizedMac
        );
        return {
            ...det,
            code: codeEntry?.code,
            codeName: codeEntry?.name,
            codeId: codeEntry?.id,
            codeEntry,
        };
    };

    const formatDetectorName = (name: string | undefined, fallback: string): string => {
        if (!name) return fallback;

        return name
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .trim();
    };

    const [regions, setRegions] = useState<string[]>([]);
    const location = useLocation();
    const preselectedRegion = location.state?.preselectedRegion || "All";
    const [selectedRegions, setSelectedRegions] = useState<string[]>([preselectedRegion]);

    const [statusFilter, setStatusFilter] = useState<"all" | "online" | "offline">("all");
    const [expandedRegions, setExpandedRegions] = useState<{ [region: string]: boolean }>({});

    const formatRegionName = (name: string) =>
        name.replace(/^detectors_/, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    // Detector region keys without "All" — used as FilterDropdown options
    const regionFilterOptions = regions.filter(r => r !== "All");

    const handleFilterChange = (filters: { [key: string]: string[] }) => {
        const selected = filters.regions || [];
        if (selected.length === 0) {
            setSelectedRegions(["All"]);
            onRegionChange?.(["All"]);
        } else {
            setSelectedRegions(selected);
            onRegionChange?.(selected);
        }
    };

    // regional detectors (per region)
    const [detectorDataRegionalized, setDetectorDataRegionalized] = useState<{ [region: string]: DetectorObject[] }>({});

    const mapRef = useRef<LeafletMap | null>(null);

    // Fetch the countries each region belongs to
    const [regionCountries, setRegionCountries] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await fetch("/api/detectordashapi/", {
                    credentials: "same-origin"
                });
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const data: RegionCountry[] = await res.json();

                const mapping: Record<string, string> = {};
                data.forEach(item => {
                    mapping[item.GROUP_NAME] = item.Belongs_to;
                });
                setRegionCountries(mapping);
            } catch (err) {
                console.error("Error fetching countries:", err);
                setRegionCountries({});
            }
        };

        fetchCountries();
    }, []);

    // Fetch regional detectors
    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const groups = await getDashboardPermissions();
                const detectorRegions = Object.keys(groups).filter(key => key.startsWith('detectors_'));

                // Only "All" — no "All + Test Detectors"
                setRegions(["All", ...detectorRegions]);

                setSelectedRegions(prev => {
                    if (prev.length > 0 && regions.includes(prev[0])) {
                        return prev;
                    }
                    if (preselectedRegion && ["All", ...detectorRegions].includes(preselectedRegion)) {
                        return [preselectedRegion];
                    }
                    return ["All"];
                });

                const detectorsData: { [region: string]: DetectorObject[] } = {};
                await Promise.all(
                    detectorRegions.map(async (regionKey) => {
                        try {
                            const res = await fetch(`/apidet/${regionKey}/`, { credentials: "same-origin" });
                            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                            const data = await res.json();

                            if (Array.isArray(data)) {
                                detectorsData[regionKey] = data;
                            } else if (data?.Message) {
                                console.warn(`No detectors in region ${regionKey}:`, data.Message);
                                detectorsData[regionKey] = [];
                            } else {
                                detectorsData[regionKey] = [];
                            }
                        } catch (err) {
                            console.error(`Error fetching detectors for region ${regionKey}:`, err);
                            detectorsData[regionKey] = [];
                        }
                    })
                );

                setDetectorDataRegionalized(detectorsData);
            } catch (err) {
                console.error("Error fetching dashboard permissions:", err);
                setRegions([]);
                setDetectorDataRegionalized({});
            }
        };

        fetchRegions();
    }, [getDashboardPermissions]);

    // Determine selected detectors ignoring duplicates
    const selectedDetectors = useMemo(() => {
        let combined: DetectorObject[];

        if (selectedRegions.length === 0) return [];

        if (selectedRegions.includes("All")) {
            combined = Object.values(detectorDataRegionalized).flat();
        } else {
            combined = selectedRegions.flatMap(region => detectorDataRegionalized[region] || []);
        }

        const deduped = Array.from(new Map(combined.map(det => [det.mac, det])).values());
        return deduped.map(enrichDetector);  // ← enrich here
    }, [selectedRegions, detectorDataRegionalized, latestCodes]);

    const filteredDetectors = useMemo(() => {
        let detectors = selectedDetectors;

        if (statusFilter === "online") {
            detectors = detectors.filter(det => det.online);
        } else if (statusFilter === "offline") {
            detectors = detectors.filter(det => !det.online);
        }

        return detectors;
    }, [selectedDetectors, statusFilter]);

    const markerRefs = useRef<{ [key: string]: any }>({});
    const openPopup = (mac: string) => {
        const marker = markerRefs.current[mac];
        if (marker) {
            marker.openPopup();
            const { lat, lng } = marker.getLatLng();
            const offsetLat = lat + 1.5;
            mapRef.current?.flyTo([offsetLat, lng], 7, { duration: 1.2 });
        }
    };

    function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) *
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    useEffect(() => {
        if (!mapRef.current || selectedRegions.length === 0) return;

        if (selectedRegions.includes("All")) {
            mapRef.current.flyTo([0.0, 0.0], 2);
            return;
        }

        if (selectedRegions.length === 1) {
            const region = selectedRegions[0];
            const detectors = (detectorDataRegionalized[region] || []).map(enrichDetector);
            if (detectors.length > 0) {
                const first = detectors[0];
                const lat = Number(first.status.gps_lat);
                const lng = Number(first.status.gps_long);
                if (!isNaN(lat) && !isNaN(lng)) {
                    mapRef.current.flyTo([lat, lng], 7);
                }
            }
            return;
        }

        let totalLat = 0;
        let totalLng = 0;
        let count = 0;
        const coords: [number, number][] = [];

        selectedRegions.forEach((region) => {
            const detectors = (detectorDataRegionalized[region] || []).map(enrichDetector);
            if (detectors.length > 0) {
                const first = detectors[0];
                const lat = Number(first.status.gps_lat);
                const lng = Number(first.status.gps_long);
                if (!isNaN(lat) && !isNaN(lng)) {
                    totalLat += lat;
                    totalLng += lng;
                    count++;
                    coords.push([lat, lng]);
                }
            }
        });

        if (count > 0) {
            const avgLat = totalLat / count;
            const avgLng = totalLng / count;

            const uniqueCountries = new Set(
                selectedRegions.map((r) => regionCountries[r]).filter(Boolean)
            );

            let zoomLevel = 4;
            if (uniqueCountries.size > 1) {
                zoomLevel = 2;
            } else {
                let maxDistance = 0;
                for (let i = 0; i < coords.length; i++) {
                    for (let j = i + 1; j < coords.length; j++) {
                        const d = haversineDistance(
                            coords[i][0], coords[i][1],
                            coords[j][0], coords[j][1]
                        );
                        if (d > maxDistance) maxDistance = d;
                    }
                }
                if (maxDistance < 300) {
                    zoomLevel = 6;
                }
            }

            mapRef.current.flyTo([avgLat, avgLng], zoomLevel);
        }
    }, [selectedRegions, detectorDataRegionalized, regionCountries]);


    // ----------------------------------------------- Chart data -----------------------------------------------

    interface RegionBarData {
        region: string;
        label: string;
        online: number;
        offline: number;
        total: number;
        percent: number;
    }

    const regionBarData = useMemo((): RegionBarData[] => {
        const regionNames = selectedRegions.includes("All")
            ? Object.keys(detectorDataRegionalized)
            : selectedRegions;

        return regionNames
            .map(region => {
                const detectors = (detectorDataRegionalized[region] || []).map(enrichDetector);
                const online = detectors.filter(d => d.online).length;
                const offline = detectors.length - online;
                const total = detectors.length;
                const percent = total > 0 ? Math.round((online / total) * 100) : 0;
                return { region, label: formatRegionName(region), online, offline, total, percent };
            })
            .sort((a, b) => b.percent - a.percent);
    }, [selectedRegions, detectorDataRegionalized]);

    // ----------------------------------------------- Chart data -----------------------------------------------


    return (
        <div className='main-hub-dash-and-map'>

            <div className="main-hub-map">
                <MapContainer
                    center={[0.00, 0.00]}
                    zoom={2}
                    style={{ width: "100%", height: "100%" }}
                    ref={mapRef}>

                    <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    {/* <SearchControl /> */}

                    {filteredDetectors.map((detector) => {
                        const lat = Number(detector.status.gps_lat);
                        const lng = Number(detector.status.gps_long);

                        if (!lat || !lng) return null;

                        return (
                            <Marker
                                key={detector.mac}
                                position={[lat, lng]}
                                icon={getColorForMarker(detector.online, Number(detector.status.memory))}
                                ref={el => (markerRefs.current[detector.mac] = el)}
                            >
                                <Popup>
                                    <img src={fnnPopupLogo} alt="" className="fnn-popup-team-dash" />
                                    <div className='new-popup-content'>
                                        <p>
                                            <strong>Detector:</strong>{" "}
                                            <span style={{ color: '#28aee1' }}>
                                                {formatDetectorName(detector.codeName, detector.status.site_name || detector.mac)}
                                            </span>
                                        </p>
                                        <p><strong>MAC:</strong> {detector.mac}</p>
                                        <p>
                                            <strong>Status:</strong>{" "}
                                            <span style={{ color: detector.online ? 'green' : 'red' }}>
                                                {detector.online ? 'Online' : 'Offline'}
                                            </span>
                                        </p>
                                        <p><strong>Latitude:</strong> {lat}</p>
                                        <p><strong>Longitude:</strong> {lng}</p>
                                        <div className='pop-changeableValues'>
                                            <strong> GPS Fix: </strong>
                                            <span style={{ color: getColorForGPSFix(detector.status.gps_fix) }}>
                                                {detector.status.gps_fix ? "Yes" : "No"}
                                            </span>
                                        </div>
                                        <div className='google-maps-button-div'
                                            onClick={() =>
                                                window.open(
                                                    `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                                                    "_blank",
                                                    "noopener,noreferrer"
                                                )
                                            }
                                        >
                                            <img src={googleMapsLogo} alt='gmaps' />
                                            <p>Google Maps</p>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>

            <div className='main-hub-dash'>
                <h2>Detector Information</h2>

                <div className='mh-filters'>
                    <FilterDropdown
                        sections={[
                            {
                                key: "regions",
                                title: "Region",
                                options: regionFilterOptions,
                                type: "checkbox",
                                formatLabel: (option) => formatRegionName(option),
                            },
                        ]}
                        onChange={handleFilterChange}
                        resetValue={{ regions: [] }}
                    />

                    <div className="main-hub-det-summary">
                        <div
                            className={`det-summary-item ${statusFilter === "all" ? "active" : ""}`}
                            onClick={() => setStatusFilter("all")}
                        >
                            <h2>Total</h2>
                            <h1 style={{ color: '#28aee1' }}>{selectedDetectors.length}</h1>
                        </div>

                        <div
                            className={`det-summary-item ${statusFilter === "online" ? "active" : ""}`}
                            onClick={() => setStatusFilter("online")}
                        >
                            <h2>Online</h2>
                            <h1 style={{ color: '#20bf55' }}>{selectedDetectors.filter(det => det.online).length}</h1>
                        </div>

                        <div
                            className={`det-summary-item ${statusFilter === "offline" ? "active" : ""}`}
                            onClick={() => setStatusFilter("offline")}
                        >
                            <h2>Offline</h2>
                            <h1 style={{ color: '#a6a6a6' }}>{selectedDetectors.filter(det => !det.online).length}</h1>
                        </div>
                    </div>

                </div>

                <div className='main-hub-sum-and-chart'>

                    <div className="main-hub-chart-wrapper">
                        <div className="mh-progress-container">
                            {regionBarData.map(({ region, label, online, offline, total, percent }) => {

                                const detectors = (detectorDataRegionalized[region] || []).map(enrichDetector);
                                const expanded = expandedRegions[region] ?? false;

                                return (
                                    <div key={region} className="mh-progress-row">

                                        <div
                                            className="mh-progress-header"
                                            onClick={() =>
                                                setExpandedRegions(prev => ({
                                                    ...prev,
                                                    [region]: !expanded
                                                }))
                                            }
                                        >
                                            <div className="mh-progress-label" title={label}>{label}</div>

                                            <div className='mh-regions-and-total'>
                                                <div className="mh-progress-value">
                                                    <span className="mh-online-count">{online}</span>
                                                    <span className="mh-sep"> | </span>
                                                    <span className="mh-total-count">{offline}</span>
                                                    <span className="mh-pct">({percent}%)</span>
                                                </div>

                                                {expanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </div>
                                        </div>

                                        <div className="mh-progress-bar">
                                            <div
                                                className="mh-progress-fill"
                                                style={{ width: `${percent}%` }}
                                            />
                                        </div>

                                        {expanded && (
                                            <div className="mh-progress-detectors">
                                                {detectors
                                                    .filter(detector => {
                                                        if (statusFilter === "online") return detector.online;
                                                        if (statusFilter === "offline") return !detector.online;
                                                        return true;
                                                    })
                                                    .map(detector => {
                                                        const hasCodeId = !!detector.codeId;
                                                        const hasCode = !!detector.code;

                                                        let displayId = detector.codeId;
                                                        let displayCode = detector.code;

                                                        if (hasCodeId && !hasCode) {
                                                            displayCode = "U";
                                                        } else if (!hasCodeId && !hasCode) {
                                                            displayId = "-";
                                                            displayCode = "T";
                                                        }

                                                        return (
                                                            <button
                                                                key={detector.mac}
                                                                title={formatDetectorName(detector.codeName, detector.status.site_name || detector.mac)}
                                                                onClick={() => openPopup(detector.mac)}
                                                                style={{
                                                                    background: detector.online
                                                                        ? "#20bf5536"
                                                                        : "#a6a6a636",
                                                                    border: `1px solid ${detector.online ? "#20bf55" : "#a6a6a6"}`
                                                                }}
                                                            >
                                                                <FaCircle
                                                                    style={{
                                                                        color: detector.online ? "#20bf55" : "#a6a6a6"
                                                                    }}
                                                                />

                                                                {displayId} {displayCode}
                                                            </button>
                                                        );
                                                    })}
                                            </div>
                                        )}

                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>

            </div>

        </div>
    );
};


export default MainHub